/* eslint-disable react/function-component-definition */
import React, {
  useRef, useState, useMemo,
  useEffect, useCallback,
} from 'react';
import { mtstatTrack } from '$/utils/mtstat';
import { useOfficialStore } from '$/store/useOfficialStore';
import { canvasRequest } from '$/api/canvasRquest';
import APP from '$/api/api';
import { getUrlSearchParams } from '$/utils';
import useSubscriblePay from '$/hooks/useSubscribePay';
import useThemeStore from '$/store/useThemeStore';
import useVipModalStore, {
  loggleVipModal,
  setEntranceCode,
  setRebateCode,
  setVipMembershipMode,
  type VipMembershipMode,
  type VipModalTrackParams,
} from '$/store/useVipModalStore';
import { useShallow } from 'zustand/react/shallow';
import { useLanguageStore } from '$/store/languageStore';
import type { ProductSeatInfo, SuperProduct } from '$/api/server.types';
import type { AgentTeamSubscriptionProductSeatInfo } from '$/domains/teamAgent/type';
import useSubscribeVipProductList from '$/hooks/useSubscribeVipProductList';
import {
  getVipModalTrackParams,
  getVipModalTrackValue,
} from '$/utils/subscribeTrack';
import classNames from 'classnames';
import {
  useEventListener,
  useMemoizedFn,
  useSize,
} from 'ahooks';
import { message } from '$/components/Message';
import Button from '../base/Button';
import IconFont from '../IconFont';
import CategoryItem from './CategoryItem';
import PayQrcodeModal from './PayQrcodeModal';
import type { QrcodeModalRef } from './PayQrcodeModal';
import ProductListLoading from './ProductListLoading';
import TeamSeatSelector from './TeamSeatSelector';
import TwoConfirmTeamOrder from './TwoConfirmTeamOrder';
import CountDown from './CountDown';
import CoreModalPricing from './CoreModalPricing';
import CompareShortDramasTable from './CompareShortDramasTable';
import ExchangeCodeModal from '../CalcRadish/ExchangeCode';
import type { ExchangeCodeRef } from '../CalcRadish/ExchangeCode';
import {
  Dialog,
  DialogContent,
} from './Dialog';
import { getSubscriptionCardWidth } from './subscriptionCardLayout';
import { VIP_MODAL_CHILD_Z_INDEX, VIP_MODAL_Z_INDEX } from './constants';
import ContactSalesModal from './ContactSalesModal';
import { isSameSubscribePlanForMembershipMode } from './subscribeLevelScope';
import {
  clampTeamSeatCount,
  getTeamSeatRangeFromSeatInfo,
  type TeamSeatRange,
} from './teamSeatLimit';
import styles from './index.module.scss';

interface ExpandSuperProduct extends SuperProduct {
  productType: 1 | 2;
  subPeriod?: number;
  sub_period?: number;
  subPeriodDesc?: string;
  sub_period_desc?: string;
  productName: string;
  product_name?: string;
  productPriceExplain?: string;
  product_price_explain?: string;
  seatInfo?: ProductSeatInfo;
  seat_info?: AgentTeamSubscriptionProductSeatInfo;
  entranceScene?: number;
  entrance_scene?: number;
}

interface VipModalProps {
  enableVip?: boolean;
  vipLevelNumber?: number | 'all';
  mode?: 'modal' | 'card';
  defaultOpen?: boolean;
  trackParams?: VipModalTrackParams;
  onCreateQrcodeBefore?: (options: { membershipMode: VipMembershipMode; }) => Promise<boolean>;
  onContactSales?: () => void;
}

type BillingMode = 'year' | 'month';

interface TeamOrderTeam {
  oid: number;
  client_ogroupid: string;
  name: string;
  role?: 1 | 2;
}

interface TeamOrderDisplay {
  planTitleText?: string;
  periodText?: string;
  validityText?: string;
  priceUnit?: string;
}

interface TeamOrderSelection {
  groupIndex: number;
  groupKey: string;
  product: ExpandSuperProduct;
  seatCount: number;
  team: TeamOrderTeam;
  teamName?: string;
  disableOtherTeams: boolean;
  showCreateTeamTip?: boolean;
  seatRange: TeamSeatRange;
  orderDisplay?: TeamOrderDisplay;
}

const TEAM_ACCOUNT_TYPE_UID = 1;
const TEAM_PURCHASE_SCENE_NORMAL = 0;
const TEAM_PURCHASE_SCENE_ADD_ON = 2;
const TEAM_PRODUCT_ENTRANCE_SCENE_ADD_ON = 2;

type TeamSeatTrackLocation = 'vipsku_page' | 'vip_confirm_page';

interface TeamListApiResponse {
  code: number;
  message?: string;
  data?: {
    list?: TeamOrderTeam[];
    team_count?: number;
    team_limit?: number;
    team_remaining?: number;
  };
}

interface TeamListForOrder {
  list: TeamOrderTeam[];
  teamLimit?: number;
}

/** 将当前会员模式转换为 Dayu 约定的会员套餐 tab 值。 */
function getVipTabTrackValue(membershipMode: VipMembershipMode) {
  return membershipMode === 'team' ? 'team_vip' : 'personal_vip';
}

/** 将当前计费周期转换为 Dayu 约定的年付/月付 tab 值。 */
function getVipBillingTrackValue(billingMode: BillingMode) {
  return billingMode === 'month' ? 'month_pay' : 'year_pay';
}

/** 上报会员弹窗套餐类型或年/月付 tab 点击。 */
function trackVipWinTabClick(params: {
  tab?: string;
  sub_tab?: string;
}) {
  mtstatTrack('roboneo_pay_vipwin_tab_click', params);
}

/** 上报团队版会员页内 SKU 席位调整。 */
function trackTeamSeatClick(params: {
  product: ExpandSuperProduct;
  seatCount: number;
  billingMode: BillingMode;
  location: TeamSeatTrackLocation;
}) {
  mtstatTrack('roboneo_pay_add_seates_click', {
    product_id: params.product.productId || '',
    seates_num: String(params.seatCount),
    sub_tab: getVipBillingTrackValue(params.billingMode),
    location: params.location,
  });
}

/** 从当前请求头上下文生成团队选项，作为团队列表接口失败时的兜底项。 */
function createTeamOptionFromOfficialState(state: {
  clientOId?: number;
  clientOGroupId?: string;
  clientOTeamName?: string;
}): TeamOrderTeam | undefined {
  if (!state.clientOId || !state.clientOGroupId) return undefined;
  return {
    oid: state.clientOId,
    client_ogroupid: state.clientOGroupId,
    name: state.clientOTeamName?.trim() || useLanguageStore.getState()
      .languageStore.smartcatTranslationText((v) => v.untitled_team),
  };
}

/** 拉取当前用户可选团队列表和团队数量上限，供下单与按钮置灰判断共用。 */
async function fetchTeamListForOrder(): Promise<TeamListForOrder> {
  const response = await canvasRequest.request({
    method: 'GET',
    url: APP.roboneo.api.teamList,
  }) as { data: TeamListApiResponse; };
  if (response.data.code === 0) {
    const data = response.data.data || {};
    return {
      list: data.list || [],
      teamLimit: data.team_limit,
    };
  }
  throw new Error(response.data.message || useLanguageStore.getState()
    .languageStore.smartcatTranslationText((v) => v.get_team_list_failed));
}

/** 按产品口径用团队列表长度判断个人空间是否达到团队数量上限。 */
function isTeamCreationLimitReached(data: TeamListForOrder) {
  if (typeof data.teamLimit === 'number') return data.list.length >= data.teamLimit;
  return false;
}

/**
 * 从订阅周期字段识别年/月付，新接口以 subPeriod 为准。
 */
function getProductBillingMode(product?: ExpandSuperProduct): BillingMode | undefined {
  const subPeriod = Number(product?.subPeriod ?? product?.sub_period);
  if (subPeriod === 1) return 'month';
  if (subPeriod === 3) return 'year';

  const subPeriodDesc = (product?.subPeriodDesc || product?.sub_period_desc)?.toLowerCase();
  if (subPeriodDesc === 'month') return 'month';
  if (subPeriodDesc === 'year') return 'year';

  return undefined;
}

/**
 * 规范化商品订阅周期，供当前套餐判断直接对齐接口 subPeriod。
 */
function getProductSubPeriod(product?: ExpandSuperProduct) {
  const subPeriod = Number(product?.subPeriod ?? product?.sub_period);
  if (subPeriod === 1 || subPeriod === 3) return subPeriod;

  const billingMode = getProductBillingMode(product);
  if (billingMode === 'month') return 1;
  if (billingMode === 'year') return 3;
  return undefined;
}

/**
 * 判断商品是否为月付。
 */
function isMonthProduct(product?: ExpandSuperProduct) {
  return getProductBillingMode(product) === 'month';
}

/**
 * 根据当前商品的订阅周期返回价格单位文案，避免使用 tab 状态代替商品真实周期。
 */
function getProductPriceUnit(
  product: ExpandSuperProduct | undefined,
  monthUnitText: string,
  yearUnitText: string,
) {
  const billingMode = getProductBillingMode(product);
  if (billingMode === 'year') return yearUnitText;
  if (billingMode === 'month') return monthUnitText;
  return '';
}
/**
 * 读取后端按钮展示用的货币符号，对应价格左侧的独立符号位置。
 */
function getProductMoneySymbol(product?: ExpandSuperProduct) {
  const productPrice = product?.productPrice as {
    moneySymbol?: string;
    money_symbol?: string;
  } | undefined;
  const buttonExplain = product?.buttonExplain as {
    moneySymbol?: string;
    money_symbol?: string;
  } | undefined;

  return buttonExplain?.moneySymbol
    || buttonExplain?.money_symbol
    || productPrice?.moneySymbol
    || productPrice?.money_symbol
    || '';
}

/**
 * 直接读取接口返回的原价展示字段，不在前端做数值转换或格式化。
 */
function getProductOriginalPrice(product?: ExpandSuperProduct) {
  return product?.productPrice?.formatOriginalPrice || '';
}

/**
 * 获取团队商品席位范围，upper_limit 为 -1 时不设置前端上限。
 */
function getTeamSeatRange(product?: ExpandSuperProduct): TeamSeatRange {
  return getTeamSeatRangeFromSeatInfo(product?.seatInfo || product?.seat_info);
}

/** 判断商品是否为后端针对用户当前会员下发的加席商品。 */
function isTeamSeatAddOnProduct(product?: ExpandSuperProduct) {
  return Number(product?.entranceScene ?? product?.entrance_scene) === TEAM_PRODUCT_ENTRANCE_SCENE_ADD_ON;
}

/** 根据团队商品类型返回订阅交易场景，加席 SKU 需要透传加购场景给收银台。 */
function getTeamPurchaseScene(product?: ExpandSuperProduct) {
  return isTeamSeatAddOnProduct(product)
    ? TEAM_PURCHASE_SCENE_ADD_ON
    : TEAM_PURCHASE_SCENE_NORMAL;
}

/** 按当前年/月付 tab 查找同周期加席 SKU。 */
function getTeamSeatAddOnProductByBillingMode(group: ExpandSuperProduct[], billingMode: BillingMode) {
  return group.find((product) => (
    isTeamSeatAddOnProduct(product)
      && getProductBillingMode(product) === billingMode
  ));
}

/** 加席响应中仅当前会员商品在同周期下使用加席 SKU，其余商品继续按年/月周期选择。 */
function getProductForEntranceScene(
  group: ExpandSuperProduct[],
  billingMode: BillingMode,
  selectedVariantKey: string | undefined,
  isAddOnScene: boolean,
) {
  const addOnProduct = isAddOnScene ? getTeamSeatAddOnProductByBillingMode(group, billingMode) : undefined;
  return addOnProduct || getProductByBillingMode(group, billingMode, selectedVariantKey);
}

/**
 * 将接口价格展示值转换成可用于团队席位合计的数字。
 */
function getProductPriceNumber(product?: ExpandSuperProduct) {
  const displayPrice = product?.buttonExplain?.price || product?.productPrice?.price || '';
  const price = Number(String(displayPrice).replace(/[^\d.-]/g, ''));
  return Number.isFinite(price) && price > 0 ? price : undefined;
}

/**
 * 按席位数计算团队套餐总价，并沿用接口下发的货币符号。
 */
function getTeamTotalPriceText(product: ExpandSuperProduct, seatCount: number) {
  const unitPrice = getProductPriceNumber(product);
  const moneySymbol = getProductMoneySymbol(product);
  if (!unitPrice || !moneySymbol) return '';
  const totalPrice = unitPrice * seatCount;
  const formattedPrice = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(totalPrice);
  return `${moneySymbol}${formattedPrice}`;
}

/**
 * 替换语言包里的简单占位符，供团队订阅动态文案复用已有翻译项。
 */
function fillTemplateText(template: string, params: Record<string, string | number>) {
  return Object.entries(params).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

/**
 * 团队商品价格单位只使用接口原文，不在前端补充每席或周期文案。
 */
function getTeamProductPriceUnit(product: ExpandSuperProduct) {
  return product.productPriceExplain || product.product_price_explain || '';
}

/**
 * 根据接口单席算力与当前席位数生成团队总算力说明，文案模板来自语言包。
 */
function getTeamPowerRate(product: ExpandSuperProduct, seatCount: number, template: string) {
  const powerPerSeat = getProductGiftCount(product);
  if (!powerPerSeat) return '';
  return fillTemplateText(template, {
    num: seatCount,
    count: Math.round(powerPerSeat * seatCount).toLocaleString(),
  });
}

/**
 * 解析后台下发的数量字段，兼容 “20,000” 或带单位的展示文案。
 */
function parseGiftCount(value?: string | number) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const normalizedValue = String(value || '').replace(/[^\d.]/g, '');
  return Number(normalizedValue) || 0;
}

/**
 * 获取接口商品标题配置中的萝卜数量。
 */
function getProductGiftCount(product?: ExpandSuperProduct) {
  const titleContent = getProductTitleContent(product);
  return parseGiftCount(titleContent?.coinNum ?? titleContent?.coin_num);
}

/**
 * 生成同一会员卡的归组 key，卡片层只按会员维度收拢，年/月和档位在卡片内二次选择。
 */
function getProductMemberGroupKey(product?: ExpandSuperProduct) {
  return product?.memberLabelName || product?.member_label_name || product?.commodityId || product?.productName || product?.product_name || '';
}

/**
 * 生成 Web 旗舰会员步骤档位分组 key，同周期同商品或同会员标签的商品才作为一组档位。
 */
function getProductStepGroupKey(product?: ExpandSuperProduct) {
  const subPeriod = product?.subPeriod ?? product?.sub_period ?? getProductBillingMode(product) ?? '';
  const productGroupKey = product?.memberLabelName || product?.member_label_name || product?.commodityId || product?.productName || product?.product_name || '';
  return `${productGroupKey}-${subPeriod}`;
}

/**
 * 生成同周期多档商品的选择 key，优先使用稳定的 productId。
 */
function getProductVariantKey(product?: ExpandSuperProduct) {
  if (!product) return '';
  return product.productId || `${product.commodityId}-${product.subPeriod ?? product.sub_period}-${getProductGiftCount(product)}-${product.productPrice?.price || ''}`;
}

/**
 * 按赠送量从小到大排列同周期商品，赠送量相同则按价格兜底排序。
 */
function sortProductsByGiftCount(products: ExpandSuperProduct[]) {
  return [...products].sort((a, b) => {
    const giftCountDiff = getProductGiftCount(a) - getProductGiftCount(b);
    if (giftCountDiff !== 0) return giftCountDiff;
    return Number(a.productPrice?.price || 0) - Number(b.productPrice?.price || 0);
  });
}

/**
 * 将当前周期商品按步骤档位口径二次分组，避免不同周期或不同商品被误当成同一条步骤线。
 */
function getStepProductGroupsByBillingMode(group: ExpandSuperProduct[], billingMode: BillingMode) {
  const billingProducts = group.filter((item) => getProductBillingMode(item) === billingMode);
  const groupedProducts = billingProducts.reduce<Record<string, ExpandSuperProduct[]>>((acc, product) => {
    const stepGroupKey = getProductStepGroupKey(product);
    if (!acc[stepGroupKey]) {
      acc[stepGroupKey] = [];
    }
    acc[stepGroupKey].push(product);
    return acc;
  }, {});

  return Object.values(groupedProducts)
    .map(sortProductsByGiftCount)
    .sort((a, b) => {
      const levelDiff = (a[0]?.subCommodityBo?.level || 0) - (b[0]?.subCommodityBo?.level || 0);
      if (levelDiff !== 0) return levelDiff;
      const commodityDiff = Number(a[0]?.commodityId || 0) - Number(b[0]?.commodityId || 0);
      if (commodityDiff !== 0) return commodityDiff;
      return getProductGiftCount(a[0]) - getProductGiftCount(b[0]);
    });
}

/**
 * 获取当前会员卡、当前周期下可选择的赠送量档位，若已有选择则优先保留该档位所在分组。
 */
function getStepProductsByBillingMode(group: ExpandSuperProduct[], billingMode: BillingMode, selectedVariantKey?: string) {
  const stepGroups = getStepProductGroupsByBillingMode(group, billingMode);
  return stepGroups.find((stepGroup) => {
    return stepGroup.some((product) => getProductVariantKey(product) === selectedVariantKey);
  }) || stepGroups[0] || [];
}

/**
 * 根据当前年/月付和赠送量档位选择接口实际下发的商品，当前周期缺失时不跨周期回退。
 */
function getProductByBillingMode(group: ExpandSuperProduct[], billingMode: BillingMode, selectedVariantKey?: string) {
  const stepProducts = getStepProductsByBillingMode(group, billingMode, selectedVariantKey);
  return stepProducts.find((item) => getProductVariantKey(item) === selectedVariantKey)
    || stepProducts[0];
}

/**
 * 构造当前会员组和付费周期的档位选择状态 key。
 */
function getVariantStateKey(groupKey: string, billingMode: BillingMode) {
  return `${groupKey}-${billingMode}`;
}

/**
 * 生成萝卜赠送档位按钮文案，优先展示可比较的数量。
 */
function getGiftOptionLabel(product: ExpandSuperProduct) {
  const giftCount = getProductGiftCount(product);
  return giftCount ? `${Math.round(giftCount)}` : '';
}

/**
 * 读取后台标题内容，兼容接口原始 snake_case 和 hook 转换后的 camelCase。
 */
function getProductTitleContent(product?: ExpandSuperProduct) {
  return product?.productTitleContent || product?.product_title_content;
}

/**
 * 读取接口下发的完整萝卜标题。
 */
function getProductTitleContentText(product?: ExpandSuperProduct) {
  const titleContent = getProductTitleContent(product);
  return titleContent?.completeTitle || titleContent?.complete_title || '';
}

/**
 * 获取套餐卡片顶部会员名称，优先使用订阅商品名并兼容后台标签字段。
 */
function getProductCardName(product?: ExpandSuperProduct) {
  return product?.productName || product?.product_name || product?.memberLabelName || product?.member_label_name || '';
}

/**
 * 判断是否为 Web 旗舰会员，只有这类商品展示萝卜步骤档位。
 */
function isWebFlagshipProduct(product?: ExpandSuperProduct) {
  return product?.subCommodityBo?.level === 4;
}

/**
 * 读取接口下发的新版卡片算力萝卜信息。
 */
function getPowerMeta(product?: ExpandSuperProduct) {
  const titleContent = getProductTitleContent(product);
  const calcPowerTitle = getProductTitleContentText(product);
  return {
    calcPowerIcon: titleContent?.icon || '',
    calcPowerTitle,
    calcPowerRate: titleContent?.unitCoinPrice || titleContent?.unit_coin_price || '',
  };
}

/**
 * 读取接口下发的价格说明，字段为空时不渲染续费文案。
 */
function getRenewNote(product?: ExpandSuperProduct) {
  const promotionDisplay = product?.productPromotionDisplay || product?.product_promotion_display;
  return promotionDisplay?.promotionPriceText || promotionDisplay?.promotion_price_text || '';
}

/**
 * 渲染订阅弹窗，并统一承接订阅购买链路的额外埋点参数。
 */
const VipModal = (props: VipModalProps) => {
  const {
    enableVip = true, vipLevelNumber = 'all', mode = 'modal', defaultOpen = false, trackParams, onCreateQrcodeBefore, onContactSales,
  } = props;
  const {
    open, modalType, openMode, membershipMode, entranceScene, trackParams: storeTrackParams,
  } = useVipModalStore(useShallow((state) => ({
    open: state.open,
    modalType: state.modalType,
    openMode: state.openMode,
    membershipMode: state.membershipMode,
    entranceScene: state.entranceScene,
    trackParams: state.trackParams,
  })));
  const languageStore = useLanguageStore((state) => state.languageStore);
  const userInfo = useOfficialStore((state) => state.userInfo);
  const subscribeInfo = useOfficialStore((state) => state.userInfo.subscribe);
  const loadSubscribeInfo = useOfficialStore((state) => state.loadSubscribeInfo);
  const clientOTeamName = useOfficialStore((state) => state.clientOTeamName);
  const isTeamSubscribeScope = useOfficialStore((state) => Boolean(state.clientOId));
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [selectedBillingMode, setSelectedBillingMode] = useState<BillingMode>('year');
  const [selectedGiftProductMap, setSelectedGiftProductMap] = useState<Record<string, string>>({});
  const [teamSeatCountMap, setTeamSeatCountMap] = useState<Record<string, number>>({});
  const [teamCreationLimitReached, setTeamCreationLimitReached] = useState(false);
  const [teamOrderOpen, setTeamOrderOpen] = useState(false);
  const [teamOrderSelection, setTeamOrderSelection] = useState<TeamOrderSelection>();
  const [teamOrderPaying, setTeamOrderPaying] = useState(false);
  const teamOrderPayingRef = useRef(false);
  const [contactSalesOpen, setContactSalesOpen] = useState(false);
  const [canScrollSubscriptionLeft, setCanScrollSubscriptionLeft] = useState(false);
  const [canScrollSubscriptionRight, setCanScrollSubscriptionRight] = useState(false);
  const [, setGroupRecurringMonths] = useState<Record<number, boolean>>({});
  const payQrCodeRef = useRef<QrcodeModalRef>(null);
  const exchangeCodeRef = useRef<ExchangeCodeRef>(null);
  const subscriptionCategoryRef = useRef<HTMLDivElement>(null);
  const subscriptionCategorySize = useSize(subscriptionCategoryRef);
  const params = getUrlSearchParams();
  const roomId = params.get('room_id') as string;
  const fromSource = params.get('from_source') || openMode;
  // 合并 store 和组件入参的埋点参数，外部传入值优先级更高。
  const modalTrackParams = useMemo(() => {
    return getVipModalTrackParams({
      ...(storeTrackParams || {}),
      ...(trackParams || {}),
    });
  }, [storeTrackParams, trackParams]);
  const {
    transferId: vipTransferId,
    productList: vipProductList,
    productExt: vipProductExt,
    subBtnText: vipSubBtnText,
    refetch: refetchVip,
    loading,
  } = useSubscribeVipProductList({
    useTeamHeaders: membershipMode === 'team',
    entranceScene,
    trackParams: modalTrackParams,
  });

  const theme = useThemeStore((state) => state.theme);
  const isOversea = useOfficialStore((state) => state.isOversea());
  const shouldCheckTeamCreationLimit = enableVip
    && membershipMode === 'team'
    && !isTeamSubscribeScope
    && (mode === 'card' ? defaultOpen : open && modalType === 'subscribe');

  // 商品列表保持完整；加席响应只在当前会员商品组内优先使用加席 SKU。
  const activeVipProductList = useMemo(() => {
    return Array.isArray(vipProductList) ? vipProductList as ExpandSuperProduct[] : [];
  }, [vipProductList]);
  const hasTeamSeatAddOnProduct = useMemo(() => {
    return activeVipProductList.some(isTeamSeatAddOnProduct);
  }, [activeVipProductList]);
  const isTeamSeatAddOnScene = membershipMode === 'team'
    && hasTeamSeatAddOnProduct;

  // 按接口周期字段先拆出年会员和月会员数组，后续分组与选择都基于这两个数组。
  const {
    year: yearVipProductList,
    month: monthVipProductList,
  } = useMemo(() => {
    const billingProductListMap: Record<BillingMode, ExpandSuperProduct[]> = {
      year: [],
      month: [],
    };
    if (!Array.isArray(activeVipProductList)) return billingProductListMap;

    activeVipProductList.forEach((product) => {
      const expandProduct = product as ExpandSuperProduct;
      if (isTeamSeatAddOnScene && isTeamSeatAddOnProduct(expandProduct)) return;
      const billingMode = getProductBillingMode(expandProduct);
      if (!billingMode) return;
      billingProductListMap[billingMode].push(expandProduct);
    });

    billingProductListMap.year.sort((a, b) => Number(a.commodityId) - Number(b.commodityId));
    billingProductListMap.month.sort((a, b) => Number(a.commodityId) - Number(b.commodityId));
    return billingProductListMap;
  }, [activeVipProductList, isTeamSeatAddOnScene]);

  // 等级vip  二维数组
  const levelsProductList = useMemo(() => {
    const addOnProductList = isTeamSeatAddOnScene
      ? activeVipProductList.filter(isTeamSeatAddOnProduct)
      : [];
    const visibleProductList = [...yearVipProductList, ...monthVipProductList, ...addOnProductList];
    const sortedList = [...visibleProductList]
      .sort((a, b) => Number(a.commodityId) - Number(b.commodityId));

    // Web 旗舰会员会有同会员、同周期、多档赠送量商品，按会员名优先收拢后再展示档位选择。
    const groupedByCommodity = sortedList.reduce<Record<string, ExpandSuperProduct[]>>((acc, product) => {
      const groupKey = getProductMemberGroupKey(product);
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(product);
      return acc;
    }, {});
      // 转换为数组形式，每个元素代表一个commodity_id组
    return Object.values(groupedByCommodity);
  }, [activeVipProductList, isTeamSeatAddOnScene, monthVipProductList, yearVipProductList]);

  // 每个等级的标志 获取所有不同的会员商品组 key，并按会员等级排序
  const commodityGroups = useMemo(() => {
    // eslint-disable-next-line no-unsafe-optional-chaining
    return [...levelsProductList].sort((a, b) => (a[0]?.subCommodityBo.level - b[0]?.subCommodityBo.level))
      .map((group) => Array.isArray(group) && group.length > 0 && getProductMemberGroupKey(group[0]))
      .filter((groupKey): groupKey is string => Boolean(groupKey)); // 过滤掉无效值
  }, [levelsProductList]);

  // 商品组变化时重置档位选择，避免复用上一批接口数据的 productId。
  useEffect(() => {
    setSelectedGiftProductMap({});
    setTeamSeatCountMap({});
    setSelectedGroupIndex(0);
  }, [levelsProductList]);

  // 初始化每个组的recurringMonth状态
  useEffect(() => {
    if (levelsProductList.length > 0) {
      const initialStates: Record<number, boolean> = {};
      // 新版订阅默认展示年付，月付入口由顶部 tab 全局切换。
      levelsProductList.forEach((item, index) => {
        initialStates[index] = selectedBillingMode === 'month'
          && item.findIndex(isMonthProduct) !== -1;
      });
      setGroupRecurringMonths(initialStates);
    }
  }, [levelsProductList, commodityGroups, selectedBillingMode]);

  // 根据当前会员等级和付费周期选择会员订阅商品。
  const selectedProduct = useMemo(() => {
    const currentGroup = levelsProductList[selectedGroupIndex] || [];
    const groupKey = getProductMemberGroupKey(currentGroup[0]);
    return getProductForEntranceScene(
      currentGroup,
      selectedBillingMode,
      selectedGiftProductMap[getVariantStateKey(groupKey, selectedBillingMode)],
      isTeamSeatAddOnScene,
    );
  }, [isTeamSeatAddOnScene, levelsProductList, selectedGiftProductMap, selectedGroupIndex, selectedBillingMode]);

  const { handlePay, payType, clearIntervalTimer } = useSubscriblePay(
    selectedProduct,
    vipTransferId,
    () => {
      payQrCodeRef.current?.closePayQrcode?.();
      setTeamOrderOpen(false);
      loggleVipModal(false);
    },
    modalTrackParams,
  );
  const [payQrcode, setPayQrcode] = useState('');

  // 只在订阅弹窗打开时刷新当前会员信息和会员商品，避免单购弹窗打开时误请求订阅商品。
  useEffect(() => {
    const shouldRefreshVip = (open && modalType === 'subscribe') || defaultOpen;

    if (shouldRefreshVip) {
      loadSubscribeInfo().catch((error) => {
        console.error('刷新会员信息失败:', error);
      });
      refetchVip();
    }
    if (!open) {
      setSelectedBillingMode('year');
      setTeamOrderOpen(false);
      setTeamOrderSelection(undefined);
      setTeamOrderPaying(false);
      teamOrderPayingRef.current = false;
      setRebateCode('');
    }
  }, [defaultOpen, loadSubscribeInfo, modalType, open, refetchVip]);

  /**
   * 个人空间进入团队套餐时刷新团队数量上限，用于达到上限时禁用升级按钮。
   */
  useEffect(() => {
    let canceled = false;

    if (!shouldCheckTeamCreationLimit) {
      setTeamCreationLimitReached(false);
    } else {
      fetchTeamListForOrder()
        .then((data) => {
          if (canceled) return undefined;
          setTeamCreationLimitReached(isTeamCreationLimitReached(data));
          return undefined;
        })
        .catch((error) => {
          if (canceled) return undefined;
          console.error('获取团队数量上限失败:', error);
          setTeamCreationLimitReached(false);
          return undefined;
        });
    }

    return () => {
      canceled = true;
    };
  }, [shouldCheckTeamCreationLimit]);

  /**
   * 使用当前卡片选中的真实商品创建订单，支持同会员多赠送量档位。
   */
  const handleSubscribe = async (
    groupIndex?: number,
    payProduct?: ExpandSuperProduct,
    quantity?: number,
    options?: {
      team?: TeamOrderTeam;
      disableOtherTeams?: boolean;
      orderDisplay?: TeamOrderDisplay;
    },
  ) => {
    if (groupIndex !== undefined) {
      setSelectedGroupIndex(groupIndex);
    }
    const isTeamPurchase = membershipMode === 'team';
    const previousOfficialState = useOfficialStore.getState();
    const hadTeamBeforeCreateQrcode = Boolean(
      previousOfficialState.clientOId && previousOfficialState.clientOGroupId,
    );
    if (onCreateQrcodeBefore) {
      const res = await onCreateQrcodeBefore?.({ membershipMode });
      if (!res) {
        return;
      }
    }
    const latestOfficialState = useOfficialStore.getState();
    const officialTeam = createTeamOptionFromOfficialState(latestOfficialState);
    const orderTeam = options?.team || officialTeam;
    const disableOtherTeams = options?.disableOtherTeams ?? hadTeamBeforeCreateQrcode;
    const showCreateTeamTip = isTeamPurchase && !hadTeamBeforeCreateQrcode && Boolean(orderTeam);
    if (isTeamPurchase && !orderTeam) {
      message.error(languageStore.smartcatTranslationText(
        (v) => v.current_team_info_unavailable_select_or_refresh_retry,
      ));
      return;
    }
    let teamSeatCount = quantity;
    if (isTeamPurchase && payProduct) {
      const seatRange = getTeamSeatRange(payProduct);
      const seatCount = quantity ?? seatRange.defaultCount;
      teamSeatCount = seatCount;
      setTeamOrderSelection({
        groupIndex: groupIndex ?? selectedGroupIndex,
        groupKey: getProductMemberGroupKey(payProduct),
        product: payProduct,
        seatCount,
        team: orderTeam as TeamOrderTeam,
        teamName: orderTeam?.name,
        disableOtherTeams,
        showCreateTeamTip,
        seatRange,
        orderDisplay: options?.orderDisplay,
      });
      if (isOversea) {
        setTeamOrderOpen(true);
        return;
      }
    }
    const res = await handlePay?.(payProduct, {
      oid: isTeamPurchase ? orderTeam?.oid : undefined,
      groupId: isTeamPurchase ? orderTeam?.client_ogroupid : undefined,
      accountId: isTeamPurchase ? userInfo.uid : undefined,
      accountType: isTeamPurchase ? TEAM_ACCOUNT_TYPE_UID : undefined,
      purchaseScene: isTeamPurchase ? getTeamPurchaseScene(payProduct) : undefined,
      quantity: isTeamPurchase ? teamSeatCount : undefined,
    });
    if (res) {
      if (payType === 'qrcode') {
        setPayQrcode(res.url);
        if (isTeamPurchase) {
          setTeamOrderOpen(true);
        } else {
          payQrCodeRef.current?.openPayQrcode?.();
        }
      }
    }
  };

  /**
   * 个人版购买前校验自动续费会员的原购买渠道，并只拦截当前同等级同周期套餐的重复购买。
   */
  const handleSubscribeClick = (
    groupIndex: number,
    payProduct: ExpandSuperProduct,
    quantity?: number,
    options?: { orderDisplay?: TeamOrderDisplay; },
  ) => {
    if (membershipMode === 'team') {
      handleSubscribe(groupIndex, payProduct, quantity, options);
      return;
    }

    if (!subscribeInfo?.hasRecurringMonth) {
      handleSubscribe(groupIndex, payProduct, quantity);
      return;
    }

    if ((subscribeInfo.renewInfo.pay_channel === 'alipay' && isOversea)
      || (subscribeInfo.renewInfo.pay_channel === 'stripe' && !isOversea)) {
      message.error(languageStore.smartcatTranslationText((v) => v.Farilure_try_again));
      return;
    }
    if (subscribeInfo.renewInfo.platform === 2) {
      message.error(languageStore.smartcatTranslationText((v) => v.Failure_appstore));
      return;
    }
    if (subscribeInfo.renewInfo.platform === 3) {
      message.error(languageStore.smartcatTranslationText((v) => v.Fariure_googleplay));
      return;
    }
    if (subscribeInfo.renewInfo.platform !== 4 && subscribeInfo.renewInfo.platform !== 0) {
      message.error(languageStore.smartcatTranslationText((v) => v.Farilure_try_again));
      return;
    }
    if (isSameSubscribePlanForMembershipMode(
      membershipMode,
      payProduct?.subCommodityBo?.level,
      getProductSubPeriod(payProduct),
      subscribeInfo.level,
      subscribeInfo.subPeriod,
      isTeamSubscribeScope,
    )) {
      message.info(languageStore.smartcatTranslationText((v) => v.user_suscribed));
      return;
    }

    handleSubscribe(groupIndex, payProduct, quantity);
  };

  /**
   * 切换个人版与团队版商品集合，并回到设计默认的年付状态。
   */
  const handleMembershipModeChange = (nextMode: VipMembershipMode) => {
    setVipMembershipMode(nextMode);
    setSelectedBillingMode('year');
    setSelectedGroupIndex(0);
    trackVipWinTabClick({
      tab: getVipTabTrackValue(nextMode),
    });
    mtstatTrack('pay_win_member_type_click', {
      member_type: nextMode,
    });
  };

  /**
   * 在接口约束范围内调整单张团队套餐的席位数。
   */
  const handleTeamSeatCountChange = (
    groupKey: string,
    product: ExpandSuperProduct,
    nextCount: number,
    trackLocation?: TeamSeatTrackLocation,
  ) => {
    const seatRange = getTeamSeatRange(product);
    const safeCount = clampTeamSeatCount(seatRange, nextCount);
    setTeamSeatCountMap((prev) => {
      return {
        ...prev,
        [groupKey]: safeCount,
      };
    });
    if (trackLocation) {
      trackTeamSeatClick({
        product,
        seatCount: safeCount,
        billingMode: selectedBillingMode,
        location: trackLocation,
      });
    }
  };

  /** 修改确认弹窗席位后同步套餐卡；国内重建二维码，海外等待用户最终确认后再下单。 */
  const handleTeamOrderSeatCountChange = (nextCount: number) => {
    if (!teamOrderSelection) return;
    const {
      groupIndex, groupKey, product, seatRange, team, disableOtherTeams, orderDisplay,
    } = teamOrderSelection;
    const safeCount = clampTeamSeatCount(seatRange, nextCount);
    trackTeamSeatClick({
      product,
      seatCount: safeCount,
      billingMode: selectedBillingMode,
      location: 'vip_confirm_page',
    });
    handleTeamSeatCountChange(groupKey, product, safeCount);
    setTeamOrderSelection((currentSelection) => (currentSelection ? {
      ...currentSelection,
      seatCount: safeCount,
    } : currentSelection));
    if (isOversea) return;
    handleSubscribe(groupIndex, product, safeCount, { team, disableOtherTeams, orderDisplay });
  };

  /** 海外团队订单在二次确认按钮点击后才创建 Stripe 支付链接。 */
  const handleTeamOrderPay = async () => {
    if (!isOversea || !teamOrderSelection || !handlePay || teamOrderPayingRef.current) return;

    const {
      product,
      seatCount,
      team,
    } = teamOrderSelection;
    teamOrderPayingRef.current = true;
    setTeamOrderPaying(true);
    try {
      await handlePay(product, {
        oid: team.oid,
        groupId: team.client_ogroupid,
        accountId: userInfo.uid,
        accountType: TEAM_ACCOUNT_TYPE_UID,
        purchaseScene: getTeamPurchaseScene(product),
        quantity: seatCount,
      });
    } finally {
      teamOrderPayingRef.current = false;
      setTeamOrderPaying(false);
    }
  };

  /** 关闭团队订单弹窗时停止当前二维码轮询，避免隐藏订单继续占用定时器。 */
  const handleTeamOrderOpenChange = (nextOpen: boolean) => {
    setTeamOrderOpen(nextOpen);
    if (!nextOpen) {
      clearIntervalTimer?.();
      setPayQrcode('');
      setTeamOrderSelection(undefined);
      setTeamOrderPaying(false);
      teamOrderPayingRef.current = false;
    }
  };

  /**
   * 上报企业服务入口点击并打开联系销售弹窗。
   */
  const handleContactSalesClick = () => {
    mtstatTrack('pay_win_contact_sales_click', {
      member_type: 'team',
    });
    setContactSalesOpen(true);
    onContactSales?.();
  };

  /**
   * 切换新版顶部年付/月付 tab，并同步各商品组的续订选择状态。
   */
  const handleBillingModeChange = (billingMode: BillingMode) => {
    setSelectedBillingMode(billingMode);
    const isMonth = billingMode === 'month';
    setGroupRecurringMonths((prev) => {
      const nextStates = { ...prev };
      levelsProductList.forEach((group, index) => {
        nextStates[index] = isMonth && group.some(isMonthProduct);
      });
      return nextStates;
    });
    trackVipWinTabClick({
      tab: getVipTabTrackValue(membershipMode),
      sub_tab: getVipBillingTrackValue(billingMode),
    });
    mtstatTrack('pay_win_renew_click', {
      button_type: isMonth ? 'open' : 'close',
      group_index: -1,
    });
  };

  /**
   * 汇总订阅弹窗曝光埋点参数，允许业务入参覆盖来源字段。
   */
  const getPayWinShowTrackParams = useCallback(() => {
    return {
      ...modalTrackParams,
      room_id: getVipModalTrackValue(modalTrackParams, 'room_id', roomId),
      from_source: getVipModalTrackValue(modalTrackParams, 'from_source', fromSource),
      position_type: getVipModalTrackValue(modalTrackParams, 'position_type', window.location.pathname),
    };
  }, [fromSource, modalTrackParams, roomId]);

  useEffect(() => {
    if (open && modalType === 'subscribe') {
      mtstatTrack('pay_win_show', getPayWinShowTrackParams());
    }
    if (!open) {
      setEntranceCode('default');
    }
  }, [open, modalType, subscribeInfo?.subType, getPayWinShowTrackParams]);
  const showCommodityGroups = useMemo(() => {
    if (vipLevelNumber !== 'all') {
      return commodityGroups.slice(0, vipLevelNumber);
    }
    return commodityGroups;
  }, [commodityGroups, vipLevelNumber]);

  /**
   * 仅禁用用户当前同等级且同周期套餐，其他套餐保持可购买。
   */
  const getSubscribeButtonState = (
    productLevel: number,
    productSubPeriod?: number,
    subscribeLevel?: number,
    subscribeSubPeriod?: number,
  ) => {
    if (isSameSubscribePlanForMembershipMode(
      membershipMode,
      productLevel,
      productSubPeriod,
      subscribeLevel,
      subscribeSubPeriod,
      isTeamSubscribeScope,
    )) {
      return {
        text: languageStore.smartcatTranslationText((v) => v.your_current_plan),
        disabled: true,
      };
    }
    return {
      text: languageStore.smartcatTranslationText((v) => v.subscribe_to_vip),
      disabled: false,
    };
  };

  /** 根据当前横向滚动位置同步左右按钮可见性。 */
  const updateSubscriptionScrollState = useMemoizedFn(() => {
    const categoryElement = subscriptionCategoryRef.current;
    if (!categoryElement) return;

    setCanScrollSubscriptionLeft(categoryElement.scrollLeft > 1);
    setCanScrollSubscriptionRight(
      categoryElement.scrollLeft + categoryElement.clientWidth < categoryElement.scrollWidth - 1,
    );
  });

  useEventListener('scroll', updateSubscriptionScrollState, {
    target: subscriptionCategoryRef,
    passive: true,
  });

  // 商品、付费周期或列表尺寸变化后，同步卡片预露出宽度与横向滚动状态。
  useEffect(() => {
    const categoryElement = subscriptionCategoryRef.current;
    if (!categoryElement || loading) return;

    const cardWidth = getSubscriptionCardWidth(
      categoryElement.clientWidth,
      categoryElement.childElementCount,
    );
    categoryElement.style.setProperty('--subscription-card-width', `${cardWidth}px`);
    updateSubscriptionScrollState();
  }, [
    levelsProductList,
    loading,
    selectedBillingMode,
    showCommodityGroups,
    subscriptionCategorySize?.height,
    subscriptionCategorySize?.width,
    updateSubscriptionScrollState,
  ]);

  /** 点击左侧悬浮按钮时按一张卡片的宽度平滑返回列表。 */
  const handleSubscriptionScrollLeft = () => {
    const categoryElement = subscriptionCategoryRef.current;
    const firstCardElement = categoryElement?.firstElementChild as HTMLElement | null;
    if (!categoryElement || !firstCardElement) return;

    const columnGap = Number.parseFloat(window.getComputedStyle(categoryElement).columnGap) || 0;
    categoryElement.scrollBy({
      left: -(firstCardElement.offsetWidth + columnGap),
      behavior: 'smooth',
    });
  };

  /** 点击右侧悬浮按钮时按一张卡片的宽度平滑推进列表。 */
  const handleSubscriptionScrollRight = () => {
    const categoryElement = subscriptionCategoryRef.current;
    const firstCardElement = categoryElement?.firstElementChild as HTMLElement | null;
    if (!categoryElement || !firstCardElement) return;

    const columnGap = Number.parseFloat(window.getComputedStyle(categoryElement).columnGap) || 0;
    categoryElement.scrollBy({
      left: firstCardElement.offsetWidth + columnGap,
      behavior: 'smooth',
    });
  };

  /**
   * 渲染设计稿顶部年付/月付切换，当前只负责选择商品周期。
   */
  const renderBillingTabs = () => {
    return (
      <div className={styles['billing-tabs']}>
        <button
          type="button"
          className={classNames(styles['billing-tab'], { [styles.active]: selectedBillingMode === 'year' })}
          onClick={() => handleBillingModeChange('year')}
        >
          {languageStore.smartcatTranslationText((v) => v.yearly_billing)}
          <span>{languageStore.smartcatTranslationText((v) => v['8_8_discount'])}</span>
        </button>
        <button
          type="button"
          className={classNames(styles['billing-tab'], { [styles.active]: selectedBillingMode === 'month' })}
          onClick={() => handleBillingModeChange('month')}
        >
          {languageStore.smartcatTranslationText((v) => v.monthly_billing)}
        </button>
      </div>
    );
  };

  /**
   * 渲染订阅主弹窗的个人版/团队版一级切换。
   */
  const renderMembershipTabs = () => {
    return (
      <div className={styles['membership-tabs']}>
        <button
          type="button"
          className={classNames(styles['membership-tab'], { [styles['membership-tab-active']]: membershipMode === 'personal' })}
          onClick={() => handleMembershipModeChange('personal')}
        >
          {languageStore.smartcatTranslationText((v) => v.individual_member_plan)}
        </button>
        <button
          type="button"
          className={classNames(styles['membership-tab'], { [styles['membership-tab-active']]: membershipMode === 'team' })}
          onClick={() => handleMembershipModeChange('team')}
        >
          {languageStore.smartcatTranslationText((v) => v.team_edition_membership)}
        </button>
      </div>
    );
  };

  /**
   * 渲染团队套餐列表底部的企业定制服务入口。
   */
  const renderEnterpriseService = () => {
    return (
      <div className={styles['enterprise-service']}>
        <div className={styles['enterprise-service-info']}>
          <span className={styles['enterprise-service-icon']}>
            <IconFont name="buildingsBold" fontSize={24} color="currentColor" />
          </span>
          <span>
            <strong>{languageStore.smartcatTranslationText((v) => v.enterprise_customization_services)}</strong>
            <small>
              {languageStore.smartcatTranslationText((v) => v.support_hezhou_procurement_exclusive_services_and_corporate_compliance_needs_)}
            </small>
          </span>
        </div>
        <button type="button" className={styles['contact-sales-button']} onClick={handleContactSalesClick}>
          <IconFont name="dcardPhoto" fontSize={17} color="currentColor" />
          {languageStore.smartcatTranslationText((v) => v.contact_sales)}
        </button>
      </div>
    );
  };

  /**
   * 渲染新版会员订阅卡片列表，保留横向溢出以匹配 Figma 的五卡展示。
   */
  const renderVipCategoryCard = () => {
    if (loading) return <ProductListLoading theme={theme} />;
    if (!showCommodityGroups.length) {
      return (
        <div className={styles['subscription-empty']}>
          {membershipMode === 'team'
            ? languageStore.smartcatTranslationText((v) => v.no_team_plan_available)
            : languageStore.smartcatTranslationText((v) => v.price_load_failed_retry_later)}
        </div>
      );
    }
    return (
      <div className={styles['subscription-carousel']}>
        {canScrollSubscriptionLeft ? (
          <button
            type="button"
            className={classNames(
              styles['subscription-scroll-button'],
              styles['subscription-scroll-button-left'],
            )}
            aria-label="向左滚动套餐"
            onClick={handleSubscriptionScrollLeft}
          >
            <IconFont name="arrowLeftBold" fontSize={20} color="currentColor" />
          </button>
        ) : null}
        <div
          ref={subscriptionCategoryRef}
          className={classNames(styles['subscrible-category'], 'vip-modal-category-list')}
        >
          {(showCommodityGroups || []).map((groupKey, index: number) => {
          // 根据会员商品组 key 找到对应的商品组
            const currentCommodityGroup = levelsProductList.find(
              (group) => Array.isArray(group) && group.length > 0 && getProductMemberGroupKey(group[0]) === groupKey,
            ) as ExpandSuperProduct[] || [];

            // 获取当前组索引，购买时需要先切换 selectedGroupIndex。
            const groupIndex = levelsProductList.findIndex(
              (group) => Array.isArray(group) && group.length > 0 && getProductMemberGroupKey(group[0]) === groupKey,
            );

            // 根据顶部年/月付 tab 和赠送萝卜档位选择当前卡片对应商品。
            const giftStateKey = getVariantStateKey(groupKey, selectedBillingMode);
            const addOnProduct = isTeamSeatAddOnScene
              ? getTeamSeatAddOnProductByBillingMode(currentCommodityGroup, selectedBillingMode)
              : undefined;
            const stepProducts = addOnProduct
              ? [addOnProduct]
              : getStepProductsByBillingMode(
                currentCommodityGroup,
                selectedBillingMode,
                selectedGiftProductMap[giftStateKey],
              );
            const currentVipProduct = getProductForEntranceScene(
              currentCommodityGroup,
              selectedBillingMode,
              selectedGiftProductMap[giftStateKey],
              isTeamSeatAddOnScene,
            );
            if (!currentVipProduct) return null;
            const powerMeta = getPowerMeta(currentVipProduct);
            const giftOptions = stepProducts.map((product) => {
              const variantKey = getProductVariantKey(product);
              return {
                key: variantKey,
                label: getGiftOptionLabel(product),
                active: getProductVariantKey(currentVipProduct) === variantKey,
                onClick: () => {
                  setSelectedGiftProductMap((prev) => ({
                    ...prev,
                    [giftStateKey]: variantKey,
                  }));
                },
              };
            });
            const showCalcPowerStep = isWebFlagshipProduct(currentVipProduct) && giftOptions.length > 1;
            const memberLevel = currentVipProduct?.subCommodityBo?.level || 0;
            const memberSubPeriod = getProductSubPeriod(currentVipProduct);
            const isAddOnProduct = isTeamSeatAddOnProduct(currentVipProduct);
            const baseSubscribeButtonState = getSubscribeButtonState(
              memberLevel,
              memberSubPeriod,
              subscribeInfo?.level,
              subscribeInfo?.subPeriod,
            );
            let subscribeButtonState = baseSubscribeButtonState;
            if (isTeamSeatAddOnScene) {
              subscribeButtonState = isAddOnProduct
                ? {
                  ...baseSubscribeButtonState,
                  disabled: false,
                  text: vipSubBtnText || languageStore.smartcatTranslationText((v) => v.purchase_seat),
                }
                : {
                  ...baseSubscribeButtonState,
                  disabled: true,
                  text: languageStore.smartcatTranslationText((v) => v.upgrade_add_seat),
                };
            }
            if (membershipMode === 'team' && !isTeamSubscribeScope && teamCreationLimitReached) {
              subscribeButtonState = {
                disabled: true,
                text: languageStore.smartcatTranslationText((v) => v.team_plan_error_team_limit),
              };
            }
            const teamSeatRange = membershipMode === 'team'
              ? getTeamSeatRange(currentVipProduct)
              : undefined;
            const teamSeatCount = teamSeatRange
              ? teamSeatCountMap[groupKey] ?? teamSeatRange.defaultCount
              : undefined;
            const billingUnit = getProductPriceUnit(
              currentVipProduct,
              languageStore.smartcatTranslationText((v) => v._moon),
              languageStore.smartcatTranslationText((v) => v._year),
            );
            const cardProductName = getProductCardName(currentVipProduct);
            const teamOrderPeriodText = selectedBillingMode === 'year'
              ? languageStore.smartcatTranslationText((v) => v.yearly_billing)
              : languageStore.smartcatTranslationText((v) => v.monthly_billing);
            const teamOrderPlanTitleText = memberLevel === 4 && selectedBillingMode === 'year'
              ? languageStore.smartcatTranslationText((v) => v.flagship_membership_annual_purchase)
              : [cardProductName, teamOrderPeriodText].filter(Boolean).join(' · ');
            const teamOrderPeriodDuration = Number(
              currentVipProduct.subPeriodDuration ?? currentVipProduct.sub_period_duration,
            );
            const teamOrderValidityText = Number.isFinite(teamOrderPeriodDuration) && teamOrderPeriodDuration > 0
              ? fillTemplateText(
                languageStore.smartcatTranslationText((v) => v._count_months_after_successful_payment),
                { count: teamOrderPeriodDuration },
              )
              : teamOrderPeriodText;
            const teamProductPriceUnit = selectedBillingMode === 'year'
              ? languageStore.smartcatTranslationText((v) => v._annual_banquet)
              : getTeamProductPriceUnit(currentVipProduct);
            const teamOrderDisplay: TeamOrderDisplay | undefined = membershipMode === 'team'
              ? {
                planTitleText: teamOrderPlanTitleText,
                periodText: teamOrderPeriodText,
                validityText: teamOrderValidityText,
                priceUnit: teamProductPriceUnit,
              }
              : undefined;
            const teamSeatNode = membershipMode === 'team' && teamSeatRange && teamSeatCount !== undefined ? (
              <TeamSeatSelector
                className={styles['team-seat-selector']}
                value={teamSeatCount}
                min={teamSeatRange.minCount}
                max={teamSeatRange.maxCount}
                seatText={fillTemplateText(
                  languageStore.smartcatTranslationText((v) => v._num_seats),
                  { num: teamSeatCount },
                )}
                totalLabel={languageStore.smartcatTranslationText((v) => v.total)}
                totalText={getTeamTotalPriceText(currentVipProduct, teamSeatCount)}
                onChange={(nextCount) => handleTeamSeatCountChange(
                  groupKey,
                  currentVipProduct,
                  nextCount,
                  'vipsku_page',
                )}
              />
            ) : undefined;

            return (
              <CategoryItem
                key={groupKey}
                name={cardProductName}
                nameClass={styles.vipName}
                moneySymbol={getProductMoneySymbol(currentVipProduct)}
                price={currentVipProduct?.buttonExplain?.price || ''}
                originalPrice={getProductOriginalPrice(currentVipProduct)}
                monthUnit={membershipMode === 'team'
                  ? teamProductPriceUnit
                  : billingUnit}
                discount={currentVipProduct?.promotionBanner}
                discountTone={memberLevel >= 5 ? 'pricingBadge2' : 'pricingBadge1'}
                renewNote={getRenewNote(currentVipProduct)}
                extraPriceNode={teamSeatNode}
                calcPowerIcon={powerMeta.calcPowerIcon}
                calcPowerTitle={powerMeta.calcPowerTitle}
                calcPowerRate={membershipMode === 'team'
                  ? getTeamPowerRate(
                    currentVipProduct,
                    teamSeatCount || 0,
                    languageStore.smartcatTranslationText(
                      (v) => v.currently_num_slots_total_of_count_computational_radishes_month,
                    ),
                  )
                  : powerMeta.calcPowerRate}
                showCalcPowerStep={membershipMode === 'personal' && showCalcPowerStep}
                calcPowerOptions={giftOptions}
                productRightsDesc={currentVipProduct?.productRightsDesc}
                teamMode={membershipMode === 'team'}
                operatorNode={(
                  <Button
                    disabled={subscribeButtonState.disabled}
                    className={styles['vip-subscribe-button']}
                    buttonStyle={{ width: '100%', height: '40px' }}
                    onClick={() => handleSubscribeClick(
                      groupIndex,
                      currentVipProduct,
                      membershipMode === 'team' ? teamSeatCount : undefined,
                      { orderDisplay: teamOrderDisplay },
                    )}
                  >
                    {subscribeButtonState.text}
                  </Button>
  )}
              />
            );
          })}
        </div>
        {canScrollSubscriptionRight ? (
          <button
            type="button"
            className={classNames(
              styles['subscription-scroll-button'],
              styles['subscription-scroll-button-right'],
            )}
            aria-label="向右滚动套餐"
            onClick={handleSubscriptionScrollRight}
          >
            <IconFont name="arrowRightBold" fontSize={20} color="currentColor" />
          </button>
        ) : null}
      </div>
    );
  };

  const teamOrderDialog = (
    <TwoConfirmTeamOrder
      open={teamOrderOpen}
      qrcodeUrl={payQrcode}
      product={teamOrderSelection?.product}
      seatCount={teamOrderSelection?.seatCount}
      teamName={teamOrderSelection?.teamName || clientOTeamName}
      showCreateTeamTip={teamOrderSelection?.showCreateTeamTip}
      planTitleText={teamOrderSelection?.orderDisplay?.planTitleText}
      periodText={teamOrderSelection?.orderDisplay?.periodText}
      validityText={teamOrderSelection?.orderDisplay?.validityText}
      priceUnit={teamOrderSelection?.orderDisplay?.priceUnit}
      minSeatCount={teamOrderSelection?.seatRange.minCount}
      maxSeatCount={teamOrderSelection?.seatRange.maxCount}
      overlayZIndex={VIP_MODAL_CHILD_Z_INDEX}
      paying={teamOrderPaying}
      onOpenChange={handleTeamOrderOpenChange}
      onSeatCountChange={handleTeamOrderSeatCountChange}
      onPay={handleTeamOrderPay}
    />
  );

  return (
    mode === 'modal' ? (
      <>
        <Dialog
          open={enableVip && open && modalType === 'subscribe'}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              loggleVipModal(false);
              setGroupRecurringMonths({});
              clearIntervalTimer?.();
            }
          }}
        >
          <DialogContent
            fullscreen
            overlayZIndex={VIP_MODAL_Z_INDEX}
            className={classNames(styles['category-wrapper'], styles['subscription-dialog'])}
          >
            <div className={styles['category-container']}>
              <CountDown className={styles['countdown-banner']} />
              {renderMembershipTabs()}
              <div className={styles['vip-header']}>
                <div className={styles['vip-title']}>
                  {languageStore.smartcatTranslationText((v) => v.myip_keep_creating_your_ip)}
                </div>
                <div className={styles['vip-subtitle']}>
                  <span>
                    {languageStore.smartcatTranslationText((v) => v.myip_keep_creating_your_ip_desc)}
                  </span>
                  {membershipMode !== 'team' && (
                    <button
                      type="button"
                      className={styles['exchange-code-button']}
                      onClick={() => exchangeCodeRef.current?.open()}
                    >
                      {languageStore.smartcatTranslationText((v) => v.membership_redemption)}
                    </button>
                  )}
                </div>
              </div>
              {renderBillingTabs()}
              <div className={styles['content-modal-wrapper']}>
                { loading ? <ProductListLoading theme={theme} /> : (
                  <>
                    {renderVipCategoryCard()}
                    {membershipMode === 'team' ? renderEnterpriseService() : (
                      <>
                        <CoreModalPricing priceTable={vipProductExt?.coreModelPriceTable} />
                        <CompareShortDramasTable tableData={vipProductExt?.highQualityShortDramaTable} />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <ExchangeCodeModal ref={exchangeCodeRef} zIndex={VIP_MODAL_CHILD_Z_INDEX} />
        <PayQrcodeModal
          title={languageStore.smartcatTranslationText((v) => v.subscribe_to_vip)}
          qrcodeUrl={payQrcode}
          ref={payQrCodeRef}
          onClose={() => {}}
        />
        {teamOrderDialog}
        <ContactSalesModal open={contactSalesOpen} onOpenChange={setContactSalesOpen} />
      </>
    ) : (
      <div className={styles['category-wrapper']}>
        {renderVipCategoryCard()}
        <CoreModalPricing priceTable={vipProductExt?.coreModelPriceTable} />
        <CompareShortDramasTable tableData={vipProductExt?.highQualityShortDramaTable} />
        <PayQrcodeModal
          title={languageStore.smartcatTranslationText((v) => v.subscribe_to_vip)}
          qrcodeUrl={payQrcode}
          ref={payQrCodeRef}
          onClose={() => {}}
        />
        {teamOrderDialog}
        <ContactSalesModal open={contactSalesOpen} onOpenChange={setContactSalesOpen} />
      </div>
    )
  );
};
export default VipModal;
